// Indicadores de despacho (PO1-3495, épico PO1-3493): regras puras da tela —
// etapas (cards e pílulas), formatação do tempo parado, leitura da URL.
// Sem React, para testar isolado.
import moment from "moment";

export const PERMISSAO_VER = "hub.operacao.despacho.visualizar";

export const ROTA_PEDIDO = "/app/trackfy/operacao/pedido";
export const ROTA_LOTES = "/app/trackfy/despacho/lotes";

// Cards da tela, na ordem. `valor` é o código que a API aceita em `etapa` e o
// nome da chave em `cards`. Uma etapa por vez no filtro.
export const ETAPAS = [
  {
    valor: "falha",
    rotulo: "Etiqueta com falha",
    tom: "pink",
    icone: "tim-icons icon-alert-circle-exc",
    ajuda: "A transportadora recusou a etiqueta. Corrija e reenvie em Despacho › Lotes › Com erro.",
  },
  {
    valor: "emProcessamento",
    rotulo: "Etiqueta em processamento",
    tom: "neutral",
    icone: "tim-icons icon-refresh-02",
    ajuda: "Pedido de etiqueta enviado e ainda sem resposta da transportadora.",
  },
  {
    valor: "foraDeLote",
    rotulo: "Emitida fora de lote",
    tom: "warning",
    icone: "tim-icons icon-tag",
    ajuda: "Etiqueta emitida e o envio ainda não entrou em nenhum lote.",
  },
  {
    valor: "loteAberto",
    rotulo: "Em lote aberto",
    tom: "info",
    icone: "tim-icons icon-components",
    ajuda: "No lote da transportadora, aguardando o fechamento (à mão ou por gatilho).",
  },
  {
    valor: "loteFechado",
    rotulo: "Lote fechado sem despacho",
    tom: "warning",
    icone: "tim-icons icon-lock-circle",
    ajuda: "Lote fechado e ainda não despachado. Despache em Despacho › Lotes › Fechados.",
  },
  {
    valor: "coletaFalha",
    rotulo: "Coleta com falha",
    tom: "pink",
    icone: "tim-icons icon-bus-front-12",
    ajuda: "O pedido de coleta foi recusado pela transportadora.",
  },
  {
    valor: "despachadoSemColeta",
    rotulo: "Despachado sem coleta",
    tom: "info",
    icone: "tim-icons icon-send",
    ajuda: "Lote despachado sem pedido de coleta registrado.",
  },
];

// A API também devolve `concluido` na lista (não é card): coleta solicitada.
const ETAPA_CONCLUIDO = { valor: "concluido", rotulo: "Coleta solicitada", tom: "success" };

const POR_VALOR = [...ETAPAS, ETAPA_CONCLUIDO].reduce((acc, e) => {
  acc[e.valor] = e;
  return acc;
}, {});

export const ehEtapa = (valor) => ETAPAS.some((e) => e.valor === valor);
export const rotuloEtapa = (valor) => (POR_VALOR[valor] ? POR_VALOR[valor].rotulo : valor || "-");
export const tomEtapa = (valor) => (POR_VALOR[valor] ? POR_VALOR[valor].tom : "neutral");

// Tempo médio por etapa (bloco da tela): colunas de `tempos`, em minutos.
export const COLUNAS_TEMPOS = [
  { chave: "nfAteEtiqueta", rotulo: "NF → etiqueta" },
  { chave: "etiquetaAteLote", rotulo: "Etiqueta → lote" },
  { chave: "loteAteFechamento", rotulo: "Lote → fechamento" },
  { chave: "fechamentoAteDespacho", rotulo: "Fechamento → despacho" },
  { chave: "despachoAteColeta", rotulo: "Despacho → coleta" },
];

// Minutos → "45 min" | "3 h 20 min" | "3 h" | "2 d 4 h" | "2 d". Sem dado: "–".
export function formatarMinutos(minutos) {
  if (minutos === null || minutos === undefined || minutos === "" || Number.isNaN(Number(minutos))) return "–";
  const total = Math.max(0, Math.round(Number(minutos)));
  if (total < 60) return `${total} min`;
  const horas = Math.floor(total / 60);
  const min = total % 60;
  if (horas < 24) return min ? `${horas} h ${min} min` : `${horas} h`;
  const dias = Math.floor(horas / 24);
  const h = horas % 24;
  return h ? `${dias} d ${h} h` : `${dias} d`;
}

const DATA = "YYYY-MM-DD";
const dataValida = (v) => (typeof v === "string" && moment(v, DATA, true).isValid() ? v : null);

// Link direto (drill-down do dashboard): `etapa`, `de`, `ate` (YYYY-MM-DD) e
// `transportadora` (id). O que não for válido é ignorado.
export function filtrosDaUrl(search) {
  const q = new URLSearchParams(search || "");
  const etapa = q.get("etapa");
  const transportadora = Number(q.get("transportadora"));
  return {
    etapa: ehEtapa(etapa) ? etapa : null,
    de: dataValida(q.get("de")),
    ate: dataValida(q.get("ate")),
    transportadora: Number.isInteger(transportadora) && transportadora > 0 ? transportadora : null,
  };
}

// Atalhos da linha: pedido na tela de Pedidos e lote na tela de Lotes.
export const linkPedido = (pedido) => `${ROTA_PEDIDO}?busca=${encodeURIComponent(pedido)}`;
export const linkLote = (idLote) => `${ROTA_LOTES}?lote=${encodeURIComponent(idLote)}`;

// Quadro do despacho (PO1-3500): fica na Torre (aba Despacho) e no Meu Painel
// (componentsDespacho/QuadroPedidos), não nesta tela; as regras dele moram aqui
// junto das etapas que ele usa.
//
// Envelhecimento do cartão no quadro, pelo tempo parado (minutos): até 4 h
// verde, até 24 h âmbar, acima disso vermelho; sem dado fica neutro.
export const LIMITE_VERDE_MIN = 240;
export const LIMITE_AMBAR_MIN = 1440;
export function tomEnvelhecimento(minutos) {
  if (minutos === null || minutos === undefined || minutos === "" || Number.isNaN(Number(minutos))) return "neutro";
  const m = Number(minutos);
  if (m <= LIMITE_VERDE_MIN) return "verde";
  if (m <= LIMITE_AMBAR_MIN) return "ambar";
  return "vermelho";
}

// Colunas do quadro em que o cartão mostra o motivo (texto da transportadora).
export const ETAPAS_COM_MOTIVO = ["falha", "coletaFalha"];

const plural = (n, um, varios) => `${Number(n) || 0} ${Number(n) === 1 ? um : varios}`;
export const textoPedidos = (n) => plural(n, "pedido", "pedidos");
// "2 NFs · 3 envios" / "1 NF · 1 envio".
export const textoNotasEnvios = (qtdNotas, qtdEnvios) => `${plural(qtdNotas, "NF", "NFs")} · ${plural(qtdEnvios, "envio", "envios")}`;
// Título do cartão: o pedido ou, para NF sem pedido, "NF <numero>".
export const tituloCartao = (item) => (item && item.pedido ? item.pedido : `NF ${(item && item.numero) || "-"}`);
